<template>
  <template v-if="summary">
    <h2>
      {{ t('achievementsThisMonth') }}
    </h2>
    <span class="text-sm text-gray-500"> {{ startDate }} - {{ endDate }} </span>
    <div class="grid flex-row gap-2 md:flex md:gap-8">
      <div class="flex-grow-1 flex flex-col gap-2" style="flex-grow: 1">
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('handovers') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('count') }}
            </div>
          </div>
          <span class="text-2xl font-semibold">
            {{ summary.handovers }}
          </span>
        </div>
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('returns') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('count') }}
            </div>
          </div>
          <span class="text-2xl font-semibold">
            {{ summary.returns }}
          </span>
        </div>
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('detailings') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('count') }}
            </div>
          </div>
          <span class="text-2xl font-semibold">
            {{ summary.detailings }}
          </span>
        </div>
      </div>
      <div class="flex flex-col gap-2" style="flex-grow: 1">
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('extrasProvisions') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('sum') }}
            </div>
          </div>
          <DashboardAgentProvisionCurrency :number="summary.extrasProvisions" />
        </div>
        <div class="item">
          <div>
            <div class="text-md font-semibold">
              {{ t('ratingProvisions') }}
            </div>
            <div class="text-xs text-gray-500">
              {{ t('sum') }}
            </div>
          </div>
          <DashboardAgentProvisionCurrency :number="summary.ratingProvisions" />
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between">
      <h2>
        {{ t('customerRatings') }}
      </h2>
      <router-link :to="{ name: 'reviews' }" class="text-link">
        {{ t('showCustomerRatings') }}
      </router-link>
    </div>
    <div class="grid flex-row gap-2 md:flex md:gap-8">
      <div class="item" style="flex-grow: 1">
        <div>
          <div class="text-md font-semibold">
            {{ t('averageRating') }}
          </div>
          <div class="text-xs text-gray-500">
            {{ t('average') }}
          </div>
        </div>
        <div class="flex flex-row align-middle">
          <span class="text-2xl font-semibold">
            {{ averageReviewRating }}
          </span>
          <span v-if="summary.averageReviewRating" class="ml-1 text-2xl font-semibold"> ★ </span>
        </div>
      </div>
      <div class="item" style="flex-grow: 1">
        <div>
          <div class="text-md font-semibold">
            {{ t('responseRate') }}
          </div>
          <div class="text-xs text-gray-500">
            {{
              t('reviewCounts', {
                answered: summary.reviewAnsweredCount,
                total: summary.reviewTotalCount,
              })
            }}
          </div>
        </div>
        <div class="flex flex-row align-middle">
          <span class="text-2xl font-semibold">
            {{ reviewResponseRate }}
          </span>
          <span class="ml-1 text-2xl font-semibold"> % </span>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <Spinner />
  </template>
</template>

<script lang="ts" setup>
import { useAgentProvisionSummary } from '@/queries/use-agent-provision-summary';
import { useI18n } from 'vue-i18n';
import DashboardAgentProvisionCurrency from '@/views/dashboard/components/DashboardAgentProvisionCurrency.vue';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import { computed } from 'vue';

const { t, n } = useI18n();

const { data: summary } = useAgentProvisionSummary();

const startDate = useFormattedDate(() => summary.value?.startDate, getDateFormat());
const endDate = useFormattedDate(() => summary.value?.endDate, getDateFormat());

const averageReviewRating = computed(() =>
  summary.value?.averageReviewRating
    ? n(summary.value.averageReviewRating, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    : '-',
);
const reviewResponseRate = computed(() =>
  summary.value
    ? Math.round(
        (summary.value.reviewTotalCount > 0
          ? summary.value.reviewAnsweredCount / summary.value.reviewTotalCount
          : 0) * 100,
      )
    : '-',
);
</script>

<style scoped>
.item {
  @apply flex flex-row items-center justify-between gap-2 rounded-md bg-white px-4 md:bg-transparent md:px-0;
}
</style>

<i18n lang="json">
{
  "en": {
    "handovers": "Handovers",
    "returns": "Returns",
    "count": "count",
    "sum": "sum",
    "extrasProvisions": "Extras provisions",
    "ratingProvisions": "5★ provisions",
    "detailings": "Detailings",
    "achievementsThisMonth": "Your achievements this month",
    "customerRatings": "Customer Ratings",
    "showCustomerRatings": "Show Ratings",
    "averageRating": "Average rating",
    "average": "Average",
    "responseRate": "Response rate",
    "reviewCounts": "{answered} out of {total}"
  },
  "de": {
    "handovers": "Übergaben",
    "returns": "Rücknahmen",
    "count": "Anzahl",
    "sum": "Summe",
    "extrasProvisions": "Extras-Provisionen",
    "ratingProvisions": "5★-Provisionen",
    "detailings": "Aufbereitungen",
    "achievementsThisMonth": "Deine Erfolge diesen Monat",
    "customerRatings": "Bewertungen Kunden",
    "showCustomerRatings": "Bewertungen anzeigen",
    "averageRating": "Bewertung",
    "average": "Durchschnitt",
    "responseRate": "Antwortquote",
    "reviewCounts": "{answered} von {total}"
  },
  "es": {
    "handovers": "Entregas",
    "returns": "Devoluciones",
    "count": "cantidad",
    "sum": "suma",
    "extrasProvisions": "Comisiones por extras",
    "ratingProvisions": "Comisiones 5★",
    "detailings": "Detailing",
    "achievementsThisMonth": "Tus resultados este mes",
    "customerRatings": "Valoraciones de clientes",
    "showCustomerRatings": "Ver valoraciones",
    "averageRating": "Valoración media",
    "average": "Media",
    "responseRate": "Tasa de respuesta",
    "reviewCounts": "{answered} de {total}"
  }
}
</i18n>
